import React, { useState, useEffect } from "react";
import {
    Drawer,
    Form,
    Input,
    Button,
    Select,
    Space,
    message,
    Row,
    Col,
    Card,
    Divider,
    Alert
} from "antd";
import {
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    NumberOutlined,
    IdcardOutlined
} from "@ant-design/icons";
import API from "../../API/API";
import dayjs from "dayjs";

const { Option } = Select;

const EditTeamDrawer = ({ visible, onClose, onUpdate, user, isAdmin }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [originalEmail, setOriginalEmail] = useState("");
    const [originalUsername, setOriginalUsername] = useState("");

    // Format phone number helper
    const formatPhoneNumber = (value) => {
        if (!value) return value;
        const cleaned = value.replace(/\D/g, "").slice(0, 11);
        if (cleaned.startsWith("1")) {
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 11)}`;
        }
        return value;
    };

    // Validate phone number
    const validatePhoneNumber = (_, value) => {
        if (!value) return Promise.reject("Phone number is required");
        const cleaned = value.replace(/\D/g, "");
        if (cleaned.length !== 11) {
            return Promise.reject("Please enter a valid 10-digit US phone number");
        }
        return Promise.resolve();
    };

    // Validate First Name
    const validateFirstName = (_, value) => {
        if (!value || !value.trim()) {
            return Promise.reject("First name is required");
        }
        if (value.trim().length < 2) {
            return Promise.reject("First name must be at least 2 characters");
        }
        if (!/^[A-Za-z\s]+$/.test(value)) {
            return Promise.reject("First name can only contain letters and spaces");
        }
        return Promise.resolve();
    };

    // Validate Last Name
    const validateLastName = (_, value) => {
        if (!value || !value.trim()) {
            return Promise.reject("Last name is required");
        }
        if (value.trim().length < 2) {
            return Promise.reject("Last name must be at least 2 characters");
        }
        if (!/^[A-Za-z\s]+$/.test(value)) {
            return Promise.reject("Last name can only contain letters and spaces");
        }
        return Promise.resolve();
    };

    // Validate Email - Only @premiumusedautoparts.com domain
    const validateEmail = (_, value) => {
        if (!value) {
            return Promise.reject("Email is required");
        }
        
        // Basic email format validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
            return Promise.reject("Please enter a valid email address");
        }

        // Check for @premiumusedautoparts.com domain
        if (!value.endsWith("@premiumusedautoparts.com")) {
            return Promise.reject("Email must be from @premiumusedautoparts.com domain");
        }

        return Promise.resolve();
    };

    // Validate Username
    const validateUsername = (_, value) => {
        if (!value) {
            return Promise.reject("Username is required");
        }
        if (value.length < 3) {
            return Promise.reject("Username must be at least 3 characters");
        }
        if (value.length > 30) {
            return Promise.reject("Username cannot exceed 30 characters");
        }
        if (!/^[a-zA-Z0-9._-]+$/.test(value)) {
            return Promise.reject("Username can only contain letters, numbers, dots, underscores, and hyphens");
        }
        return Promise.resolve();
    };

    // Auto-generate username and email from first and last name
    const generateUserFields = (firstName, lastName) => {
        if (!firstName || !lastName) return;

        const cleanFirstName = firstName.trim().toLowerCase();
        const cleanLastName = lastName.trim().replace(/\s+/g, "").toLowerCase();

        // Generate username: first letter of first name + full last name
        const username = cleanFirstName.charAt(0) + cleanLastName;
        
        // Generate email: first letter of first name + full last name @ premiumusedautoparts.com
        const email = `${cleanFirstName.charAt(0)}${cleanLastName}@premiumusedautoparts.com`;

        form.setFieldsValue({
            userName: username,
            email: email
        });
        setHasChanges(true);
    };

    // Handle first name change
    const handleFirstNameChange = (e) => {
        let value = e.target.value;
        // Capitalize first letter of each word
        value = value.replace(/\b\w/g, char => char.toUpperCase());
        form.setFieldsValue({ firstName: value });
        
        const firstName = value;
        const lastName = form.getFieldValue('lastName');
        
        // Only generate if both fields have values
        if (firstName && lastName) {
            generateUserFields(firstName, lastName);
        }
    };

    // Handle last name change
    const handleLastNameChange = (e) => {
        let value = e.target.value;
        // Capitalize first letter of each word
        value = value.replace(/\b\w/g, char => char.toUpperCase());
        form.setFieldsValue({ lastName: value });
        
        const firstName = form.getFieldValue('firstName');
        const lastName = value;
        
        // Only generate if both fields have values
        if (firstName && lastName) {
            generateUserFields(firstName, lastName);
        }
    };

    // Initialize form when user changes
    useEffect(() => {
        if (user && visible) {
            form.setFieldsValue({
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || "",
                extension: user.extension || "",
                directNumber: user.directNumber || "",
                userName: user.userName || "",
                role: user.role || "",
                subRole: user.subRole || "",
                isActive: user.isActive,
                isPerformance: user.isPerformance
            });
            
            setOriginalEmail(user.email || "");
            setOriginalUsername(user.userName || "");
        }
    }, [user, visible, form]);

    const handleCancel = () => {
        form.resetFields();
        setHasChanges(false);
        onClose();
    };

 const handleSubmit = async (values) => {
    if (!hasChanges) {
        message.info("No changes to save");
        return;
    }

    setLoading(true);
    try {
        // Prepare payload
        const payload = {
            firstName: values.firstName?.trim(),
            lastName: values.lastName?.trim(),
            extension: values.extension,
            directNumber: values.directNumber,
            role: values.role,
        };

        // CRITICAL FIX: Only include subRole if role is SALES-AGENT
        // This ensures subRole is NOT sent for ADMIN or SERVICE-AGENT
        if (values.role === "SALES-AGENT") {
            payload.subRole = values.subRole;
        } else {
            // For non-sales roles, explicitly set subRole to null or undefined
            // This will clear it in the database
            payload.subRole = null;
        }

        // Only admins can update email and username
        if (isAdmin) {
            // Validate email format before sending
            if (values.email && values.email !== originalEmail) {
                // Double-check email domain
                if (!values.email.endsWith("@premiumusedautoparts.com")) {
                    message.error("Email must be from @premiumusedautoparts.com domain");
                    setLoading(false);
                    return;
                }
                payload.email = values.email;
            }
            
            if (values.userName && values.userName !== originalUsername) {
                payload.userName = values.userName;
            }
        }

        console.log("Sending payload:", payload); // Debug log

        const response = await API.UpdateUser(user._id, payload);

        if (response.success) {
            message.success("User updated successfully");
            onUpdate(response.data || { ...user, ...payload });
            form.resetFields();
            setHasChanges(false);
            onClose();
        } else {
            message.error(response.message || "Failed to update user");
        }
    } catch (error) {
        console.error("Update error:", error);
        message.error("Failed to update user");
    } finally {
        setLoading(false);
    }
};

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EditOutlined style={{ color: '#667eea' }} />
                    <span>Edit Team Member</span>
                </div>
            }
            width={600}
            onClose={handleCancel}
            open={visible}
            styles={{ body: { paddingBottom: 80 } }}
            extra={
                <Space>
                    <Button onClick={handleCancel} icon={<CloseOutlined />}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => form.submit()}
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={loading}
                        disabled={!hasChanges}
                        style={{ background: '#667eea' }}
                    >
                        Save Changes
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onValuesChange={() => setHasChanges(true)}
            >
              

                <Divider orientation="left">
                    <UserOutlined /> Personal Information
                </Divider>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="firstName"
                            label="First Name"
                            rules={[
                                { required: true, message: "First name is required" },
                                { validator: validateFirstName }
                            ]}
                        >
                            <Input 
                                placeholder="Enter first name"
                                onChange={handleFirstNameChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="lastName"
                            label="Last Name"
                            rules={[
                                { required: true, message: "Last name is required" },
                                { validator: validateLastName }
                            ]}
                        >
                            <Input 
                                placeholder="Enter last name"
                                onChange={handleLastNameChange}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: "Email is required" },
                                { validator: validateEmail }
                            ]}
                            tooltip={!isAdmin ? "Only admins can edit email" : "Must be @premiumusedautoparts.com"}
                        >
                            <Input 
                                placeholder="Email address" 
                                disabled={!isAdmin}
                                prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="userName"
                            label="Username"
                            rules={[
                                { required: true, message: "Username is required" },
                                { validator: validateUsername }
                            ]}
                            tooltip={!isAdmin ? "Only admins can edit username" : "Auto-suggested from first & last name"}
                        >
                            <Input 
                                placeholder="Username" 
                                disabled={!isAdmin}
                                prefix={<IdcardOutlined style={{ color: '#bfbfbf' }} />}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    <PhoneOutlined /> Contact Information
                </Divider>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="extension"
                            label="Extension"
                            rules={[
                                { required: true, message: "Extension required" },
                                { pattern: /^\d{1,3}$/, message: "1-3 digits only" }
                            ]}
                        >
                            <Input 
                                placeholder="123"
                                maxLength={3}
                                prefix={<NumberOutlined style={{ color: '#bfbfbf' }} />}
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) e.preventDefault();
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={16}>
                        <Form.Item
                            name="directNumber"
                            label="Direct Number"
                            rules={[
                                { required: true, message: "Phone number required" },
                                { validator: validatePhoneNumber }
                            ]}
                        >
                            <Input
                                placeholder="+1 (XXX) XXX-XXXX"
                                maxLength={18}
                                prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                                onChange={(e) => {
                                    const formatted = formatPhoneNumber(e.target.value);
                                    form.setFieldsValue({ directNumber: formatted });
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    <IdcardOutlined /> Role Information
                </Divider>

                <Row gutter={16}>
    <Col span={12}>
        <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Select role" }]}
        >
            <Select 
                placeholder="Select role"
                onChange={(value) => {
                    // CRITICAL FIX: When role changes to non-sales, clear subRole
                    if (value !== "SALES-AGENT") {
                        form.setFieldsValue({ subRole: undefined });
                    }
                    setHasChanges(true);
                }}
            >
                <Option value="ADMIN">Admin</Option>
                <Option value="SALES-AGENT">Sales Agent</Option>
                <Option value="SERVICE-AGENT">Service Agent</Option>
            </Select>
        </Form.Item>
    </Col>
    <Col span={12}>
        <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.role !== curr.role}
        >
            {({ getFieldValue }) => {
                const role = getFieldValue('role');
                if (role === "SALES-AGENT") {
                    return (
                        <Form.Item
                            name="subRole"
                            label="Sub Role"
                            rules={[{ required: true, message: "Select sub role" }]}
                        >
                            <Select 
                                placeholder="Select tier"
                                onChange={(value) => {
                                    form.setFieldsValue({
                                        subRole: value
                                    });
                                    setHasChanges(true);
                                }}
                            >
                                <Option value="TIER-1">Tier 1</Option>
                                <Option value="TIER-2">Tier 2</Option>
                                <Option value="TIER-3">Tier 3</Option>
                            </Select>
                        </Form.Item>
                    );
                }
                return null;
            }}
        </Form.Item>
    </Col>
</Row>

                {!isAdmin && (
                    <Alert
                        message="Limited Editing"
                        description="Some fields can only be edited by administrators."
                        type="info"
                        showIcon
                        style={{ marginTop: 24, borderRadius: 12 }}
                    />
                )}
            </Form>
        </Drawer>
    );
};

export default EditTeamDrawer;
